import { motion, useScroll, useTransform } from 'framer-motion'
import { useRef, useState } from 'react'
import {
  ArrowRight,
  ChevronDown,
  Clock,
  FileText,
  GraduationCap,
  Library,
  Plus,
  Rocket,
  Search,
  Settings,
  Users,
} from 'lucide-react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import Footer from '@/components/Footer'
import ScrollReveal from '@/components/ScrollReveal'
import ParaEmpresasNavbar from '@/components/ParaEmpresasNavbar'
import AnimatedLogo from '@/components/AnimatedLogo'
import SuperAdminShowcase from '@/components/landing/SuperAdminShowcase'
import ClientLogosMarquee from '@/components/landing/ClientLogosMarquee'
import ContactForm from '@/components/landing/ContactForm'
import { usePageMeta } from '@/hooks/use-page-meta'

const TechHeroBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Grade tecnológica com fade radial */}
    <div
      className="absolute inset-0 opacity-[0.15]"
      style={{
        backgroundImage:
          'linear-gradient(hsl(18 89% 54% / 0.5) 1px, transparent 1px), linear-gradient(90deg, hsl(18 89% 54% / 0.5) 1px, transparent 1px)',
        backgroundSize: '48px 48px',
        maskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, black, transparent)',
        WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, black, transparent)',
      }}
    />

    {/* Glow radial central */}
    <div className="gradient-hero absolute inset-0" />

    {/* Partículas flutuantes */}
    <motion.div
      className="absolute top-[18%] left-[12%] w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_8px_3px_hsl(18_89%_54%/0.6)]"
      animate={{ opacity: [0.3, 1, 0.3], scale: [1, 1.4, 1] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
    />
    <motion.div
      className="absolute top-[65%] left-[8%] w-1 h-1 rounded-full bg-primary shadow-[0_0_6px_2px_hsl(18_89%_54%/0.5)]"
      animate={{ opacity: [0.2, 0.8, 0.2], scale: [1, 1.5, 1] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
    />
    <motion.div
      className="absolute top-[25%] right-[10%] w-2 h-2 rounded-full bg-primary shadow-[0_0_10px_4px_hsl(18_89%_54%/0.6)]"
      animate={{ opacity: [0.3, 1, 0.3], scale: [1, 1.3, 1] }}
      transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
    />
    <motion.div
      className="absolute top-[55%] right-[16%] w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_8px_3px_hsl(18_89%_54%/0.5)]"
      animate={{ opacity: [0.2, 0.9, 0.2], scale: [1, 1.4, 1] }}
      transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut', delay: 1.4 }}
    />

    {/* Blur central quente */}
    <motion.div
      className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[60vw] h-[40vh] bg-primary/10 rounded-full blur-3xl"
      animate={{ opacity: [0.4, 0.7, 0.4] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
    />
  </div>
)

type SynforceCardProps = {
  number: string
  eyebrow: string
  title: string
  description: string
  action: string
  delay?: number
}

const SynforceCard = ({
  number,
  eyebrow,
  title,
  description,
  action,
  delay = 0,
}: SynforceCardProps) => (
  <ScrollReveal delay={delay}>
    <div className="bg-card border border-border rounded-xl p-6 space-y-4 h-full">
      <div className="flex items-center gap-3">
        <span className="text-xs font-semibold text-primary tracking-widest">{number}</span>
        <span className="w-6 h-px bg-border" />
        <span className="text-[11px] font-semibold text-muted-foreground tracking-widest uppercase">
          {eyebrow}
        </span>
      </div>
      <h3 className="text-xl font-bold text-foreground uppercase tracking-tight">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
      <p className="text-xs font-semibold text-foreground uppercase tracking-wide flex items-center gap-1.5">
        {action} <ArrowRight className="w-3.5 h-3.5" />
      </p>
    </div>
  </ScrollReveal>
)

const ParaEmpresas = () => {
  usePageMeta({
    title: 'AI Sym para Empresas | Treinamento de Inteligência Artificial | FCamara',
    description:
      'Capacite sua empresa em Inteligência Artificial com o AI Sym, da FCamara: programa de treinamento, plataforma de aprendizagem e comunidade dedicada para elevar a maturidade em IA da sua equipe.',
    image: 'https://aisym.fcamara.com/aisym.jpeg',
  })

  const [openSteps, setOpenSteps] = useState<Record<number, boolean>>({})
  const toggleStep = (i: number) => setOpenSteps((prev) => ({ ...prev, [i]: !prev[i] }))

  const timelineRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress: timelineProgress } = useScroll({
    target: timelineRef,
    offset: ['start 85%', 'start 35%'],
  })
  const timelineLineScale = useTransform(timelineProgress, [0, 1], [0, 1])

  return (
    <div className="min-h-screen bg-background">
      <ParaEmpresasNavbar />

      {/* Hero */}
      <section className="relative min-h-[100svh] flex flex-col justify-center pt-20 pb-8 overflow-hidden">
        <TechHeroBackground />
        <div className="container relative">
          <div className="max-w-3xl mx-auto text-center space-y-10">
            <motion.h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Treinamento de <span className="text-shine">Inteligência Artificial</span> para sua
              Empresa
            </motion.h1>

            <motion.p
              className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Preparamos suas equipes do letramento à fluência em IA para conquistar produtividade e
              escala em resultados.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-3"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <a
                href="#contato"
                className="btn-primary inline-flex items-center gap-2 text-sm px-6 py-3"
              >
                Agendar diagnóstico
              </a>
              <div className="relative inline-block overflow-hidden rounded-lg p-[2px]">
                <div
                  className="absolute inset-[-1000%] animate-spin"
                  style={{
                    animationDuration: '3s',
                    background:
                      'conic-gradient(from 90deg, transparent 0%, hsl(18 89% 54%) 15%, transparent 30%)',
                  }}
                />
                <a
                  href="#como-funciona"
                  onClick={(e) => {
                    e.preventDefault()
                    document
                      .getElementById('como-funciona')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                  }}
                  className="btn-secondary relative z-10 inline-flex items-center gap-2 text-sm px-6 py-3 rounded-[7px]"
                >
                  Como funciona
                </a>
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ChevronDown className="w-9 h-9 text-primary" />
        </motion.div>
      </section>

      {/* Problema */}
      <section className="py-20 sm:py-28 bg-background">
        <div className="container">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center space-y-5">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground leading-tight">
                Sua empresa já adquiriu IA.
                <br />
                Essa foi a parte fácil.
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Todos tem acesso às melhores IAs do mundo, mas o trabalho de poucas pessoas
                realmente mudou. Extrair valor da IA é o verdadeiro desafio.
                <br />
                <span className="text-foreground font-semibold">
                  Não se preocupe, somos especialistas em conduzir sua empresa nessa missão.
                </span>
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Logo em destaque */}
      <section className="relative py-16 bg-background overflow-hidden">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-primary/20 blur-3xl" />
        <ScrollReveal>
          <div className="relative flex justify-center scale-150 sm:scale-[1.75]">
            <AnimatedLogo size="lg" />
          </div>
        </ScrollReveal>
      </section>

      {/* Como funciona */}
      <section id="como-funciona" className="py-20 sm:py-28 bg-background">
        <div className="container">
          <ScrollReveal>
            <div className="max-w-2xl mx-auto text-center mb-16">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-primary mb-4 block">
                Como funciona
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground leading-tight mb-5">
                Como conduzimos a adoção de IA na sua empresa
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Um programa em quatro etapas, ajustado ao ponto de partida real da sua operação.
              </p>
            </div>
          </ScrollReveal>

          <div
            ref={timelineRef}
            className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-7 gap-y-11"
          >
            <div className="hidden lg:block absolute top-3 left-0 right-0 h-px bg-border" />
            <motion.div
              className="hidden lg:block absolute top-3 left-0 h-px w-full origin-left bg-gradient-to-r from-primary from-[78%] to-border"
              style={{ scaleX: timelineLineScale }}
            />

            {[
              {
                num: '01',
                title: 'Avaliação',
                icon: Search,
                text: 'Mapeamos o ponto de partida real: conhecimento dos times, cultura e rotinas de trabalho. A estratégia e a governança de IA são construídas junto com a sua empresa, e todo o restante do programa é customizado a partir desses dados.',
                gain: 'Relatório de diagnóstico e estratégia de IA definida',
              },
              {
                num: '02',
                title: 'Capacitação',
                icon: GraduationCap,
                text: 'Jornadas separadas por público: liderança, times técnicos, back-office e consultoria. Cada turma trabalha desafios reais da própria área, não exercícios genéricos.',
                gain: 'Times formados e comitê de IA em operação',
              },
              {
                num: '03',
                title: 'Implementação',
                icon: Settings,
                text: 'Multiplicadores formados dentro da empresa e casos de uso testados nos times, com mentoria de especialistas. É a etapa em que o conhecimento sai da aula e entra no processo.',
                gain: 'Casos de uso rodando e multiplicadores internos ativos',
              },
              {
                num: '04',
                title: 'Rollout',
                icon: Rocket,
                text: 'Uso ampliado para toda a base de colaboradores, com método e materiais transferidos para dentro da companhia.',
                gain: 'Proficiência em IA nivelada em toda a organização',
              },
            ].map((step, i) => (
              <ScrollReveal key={step.num} delay={i * 0.1}>
                <div className="relative pt-16">
                  <div className="absolute -top-2 left-0 w-10 h-10 rounded-full bg-background border-2 border-primary flex items-center justify-center">
                    <step.icon className="w-5 h-5 text-primary" />
                  </div>
                  <span className="block text-xs font-semibold tracking-[0.14em] text-primary mb-2">
                    {step.num}
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleStep(i)}
                    aria-expanded={!!openSteps[i]}
                    className="flex items-center justify-start gap-1.5 text-left"
                  >
                    <h3 className="text-xl font-semibold text-foreground">{step.title}</h3>
                    <Plus
                      className={`w-5 h-5 flex-shrink-0 text-primary transition-transform duration-300 ${
                        openSteps[i] ? 'rotate-45' : ''
                      }`}
                    />
                  </button>
                  <div
                    aria-hidden={!openSteps[i]}
                    className={`transition-opacity duration-300 ${
                      openSteps[i] ? 'opacity-100' : 'pointer-events-none opacity-0'
                    }`}
                  >
                    <p className="text-sm text-muted-foreground leading-relaxed mt-4 mb-5">
                      {step.text}
                    </p>
                    <div className="border-t border-border pt-4">
                      <strong className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-primary mb-1.5">
                        Sua empresa tem
                      </strong>
                      <span className="text-[13px] leading-relaxed text-foreground">
                        {step.gain}
                      </span>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={0.3}>
            <div className="mt-16 mx-auto w-fit text-center">
              <span className="block text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground mb-4">
                Formatos
              </span>
              <div className="flex flex-wrap items-center justify-center gap-2.5">
                {['Presencial', 'Online', 'Workshops', 'Customizado'].map((formato) => (
                  <span
                    key={formato}
                    className="px-4 py-1.5 rounded-full border border-primary/20 bg-primary/10 text-xs font-medium text-primary"
                  >
                    {formato}
                  </span>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Plataforma */}
      <section className="py-20 sm:py-28 bg-card">
        <div className="container">
          <ScrollReveal>
            <div className="max-w-2xl mx-auto text-center mb-14">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-primary mb-4 block">
                Plataforma AI Sym
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground leading-tight mb-5">
                O que sustenta o programa no dia a dia
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Capacitação não acontece só nos encontros. Entre uma sessão e outra, seu time tem
                uma plataforma própria para continuar praticando.
              </p>
            </div>
          </ScrollReveal>

          <div className="max-w-2xl mx-auto space-y-4">
            {[
              {
                icon: Clock,
                title: 'Microlearning',
                text: 'Episódios de até 5 minutos que cabem na rotina. O time avança sem parar a operação.',
              },
              {
                icon: Library,
                title: 'Biblioteca de prompts',
                text: 'Prompts e frameworks prontos, aplicáveis ao contexto de cada área.',
              },
              {
                icon: FileText,
                title: 'Workbooks e quizzes',
                text: 'Material de apoio e verificação de aprendizagem, com acompanhamento de progresso por time.',
              },
              {
                icon: Users,
                title: 'Comunidade dedicada',
                text: 'Canal direto com especialistas FCamara e com quem está passando pelo mesmo processo.',
              },
            ].map((feature, i) => (
              <ScrollReveal key={feature.title} delay={i * 0.12}>
                <div className="flex items-start gap-5 rounded-xl border border-border bg-background p-7 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{feature.text}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Nossa Plataforma */}
      <section id="nossa-plataforma" className="py-6 sm:py-8 bg-background">
        <div className="container">
          <SuperAdminShowcase />

          <ScrollReveal>
            <div className="max-w-xl mx-auto text-center mt-4 space-y-5">
              <h3 className="text-2xl md:text-3xl font-bold text-foreground">
                Agende uma demonstração
              </h3>
              <div className="flex justify-center">
                <a
                  href="#contato"
                  onClick={(e) => {
                    e.preventDefault()
                    document
                      .getElementById('contato')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                  className="btn-primary inline-flex items-center gap-2 text-sm px-6 py-3"
                >
                  Agendar diagnóstico
                </a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Interlúdio */}
      <section className="py-20 sm:py-28 bg-secondary/20">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center space-y-3">
            <motion.p
              className="text-2xl md:text-3xl text-muted-foreground font-medium"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 0.6 }}
            >
              Transformação em IA não é uma ação isolada.
            </motion.p>
            <motion.p
              className="text-3xl md:text-4xl font-bold text-foreground"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              É um sistema.
            </motion.p>
            <motion.p
              className="text-2xl md:text-3xl text-shine font-semibold"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              E começa pela capacitação do seu time.
            </motion.p>
          </div>
        </div>
      </section>

      {/* AI Sym powered by FCamara */}
      <section className="py-20 sm:py-28 bg-background">
        <div className="container">
          <ScrollReveal>
            <div className="max-w-2xl mx-auto text-center">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-primary mb-4 block">
                AI Sym powered by FCamara
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground leading-relaxed mb-5">
                Não somos uma escola de curso de IA.
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Somos <span className="text-foreground font-semibold">FCamara</span>, um ecossistema
                de soluções em tecnologia há mais de 20 anos.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.15}>
            <div className="mx-auto mt-12 max-w-5xl space-y-4 text-center">
              <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Alguns dos nossos clientes
              </p>
              <ClientLogosMarquee />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Ecossistema Synforce */}
      <section id="synforce" className="py-20 sm:py-28 bg-background overflow-hidden">
        <div className="container">
          <ScrollReveal>
            <div className="max-w-2xl mx-auto mb-14 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground leading-tight mb-4">
                Somos especialistas em IA
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Da preparação do negócio à operação de soluções de IA, o Synforce conecta as
                capacidades necessárias para transformar intenção em impacto real.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px_1fr] gap-6 items-center">
            <div className="space-y-6 order-2 lg:order-1">
              <SynforceCard
                number="01"
                eyebrow="Learning & Enablement"
                title="AI Sym"
                description="Capacitação e letramento para preparar pessoas e negócios para trabalhar com IA."
                action="Preparar"
              />
              <SynforceCard
                number="02"
                eyebrow="Orchestration"
                title="Symphony"
                description="Infraestrutura para governar, integrar e orquestrar o uso de IA."
                action="Governar"
                delay={0.1}
              />
            </div>

            <div className="order-1 lg:order-2 flex justify-center py-8 lg:py-0">
              <ScrollReveal delay={0.2}>
                <div className="relative w-56 h-56 rounded-full flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full bg-primary/10 blur-2xl" />
                  <div className="absolute inset-0 rounded-full border border-primary/20" />
                  <div className="absolute inset-4 rounded-full border border-primary/10" />
                  <div className="relative text-center space-y-1.5 px-4">
                    <p className="text-lg font-bold text-foreground">SYNFORCE</p>
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wide leading-snug">
                      AI Transformation
                      <br />
                      Ecosystem
                    </p>
                    <motion.p
                      className="text-[11px] text-primary font-semibold uppercase tracking-wide flex items-center justify-center gap-1.5 pt-1"
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                      System active
                    </motion.p>
                  </div>
                </div>
              </ScrollReveal>
            </div>

            <div className="space-y-6 order-3">
              <SynforceCard
                number="03"
                eyebrow="Digital Workforce"
                title="Synthetic Workforce"
                description="Colaboradores sintéticos especializados para executar operações e processos."
                action="Executar"
                delay={0.15}
              />
              <SynforceCard
                number="04"
                eyebrow="AI-powered Teams"
                title="Squad Sintética"
                description="Squads potencializadas por agentes de IA trabalhando junto a talentos humanos."
                action="Escalar"
                delay={0.25}
              />
            </div>
          </div>

          <ScrollReveal delay={0.3}>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-14 pt-6 border-t border-border text-xs">
              <div className="flex items-center gap-2 font-semibold uppercase tracking-wide">
                <span className="text-primary">Prepare</span>
                <ArrowRight className="w-3 h-3 text-muted-foreground" />
                <span className="text-muted-foreground">Govern</span>
                <ArrowRight className="w-3 h-3 text-muted-foreground" />
                <span className="text-muted-foreground">Execute</span>
                <ArrowRight className="w-3 h-3 text-muted-foreground" />
                <span className="text-muted-foreground">Scale</span>
              </div>
              <p className="text-muted-foreground uppercase tracking-wide text-center sm:text-right">
                AI Sym + Symphony + Synthetic Workforce + Squad = Synforce
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Contato */}
      <section id="contato" className="py-20 sm:py-28 bg-background">
        <div className="container">
          <ScrollReveal>
            <div className="max-w-xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                  Vamos conversar sobre a sua empresa
                </h2>
                <p className="text-muted-foreground mt-3">
                  Preencha o formulário e nosso time comercial entra em contato.
                </p>
              </div>

              <ContactForm />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 sm:py-28 bg-background">
        <div className="container">
          <ScrollReveal>
            <div className="max-w-2xl mx-auto text-center mb-14">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-primary mb-4 block">
                Perguntas frequentes
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground leading-tight">
                O que as empresas perguntam antes de começar
              </h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <Accordion type="single" collapsible className="max-w-3xl mx-auto">
              {[
                {
                  q: 'Nossos times não têm base técnica. Isso é um problema?',
                  a: 'Não. A capacitação é segmentada por público e ajustada ao nível de conhecimento identificado no diagnóstico. Back-office e liderança seguem trilhas diferentes das dos times técnicos.',
                },
                {
                  q: 'As equipes são capacitadas para o uso seguro de IA e em conformidade com a LGPD?',
                  a: 'Governança entra desde o início, não no final. O programa estrutura um comitê de IA com participação de jurídico e compliance, define políticas de uso, controles de acesso e rastreamento de decisões.',
                },
                {
                  q: 'Já temos ferramentas de IA contratadas. O AI Sym serve para nós?',
                  a: 'Serve, e normalmente é o cenário mais comum. Ferramenta contratada sem capacitação em IA gera uso disperso e sem governança. O AI Sym se adapta às ferramentas existentes na sua empresa.',
                },
                {
                  q: 'O que acontece quando o programa termina?',
                  a: (
                    <>
                      Sua empresa passa a ser proficiente em IA: com multiplicadores formados,
                      comitê de IA operando, casos de uso implementados e método replicado. Conheça
                      nossas outras soluções em IA no Ecossistema{' '}
                      <a
                        href="#synforce"
                        onClick={(e) => {
                          e.preventDefault()
                          document
                            .getElementById('synforce')
                            ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                        }}
                        className="text-primary underline hover:no-underline"
                      >
                        Synforce
                      </a>
                      , da FCamara.
                    </>
                  ),
                },
                {
                  q: 'Por onde minha empresa pode começar?',
                  a: (
                    <>
                      Começa pelo{' '}
                      <a
                        href="#contato"
                        onClick={(e) => {
                          e.preventDefault()
                          document
                            .getElementById('contato')
                            ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                        }}
                        className="text-primary underline hover:no-underline"
                      >
                        diagnóstico
                      </a>
                      , uma conversa com um especialista para entender o contexto da sua empresa. A
                      partir dele, montamos uma proposta dimensionada ao seu cenário, que pode ser
                      contratada por fase ou completa.
                    </>
                  ),
                },
              ].map((item, i) => (
                <AccordionItem key={item.q} value={`item-${i}`} className="border-border">
                  <AccordionTrigger className="text-left text-base font-medium text-foreground hover:no-underline py-6">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default ParaEmpresas
